import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flame, ShieldCheck, MailCheck, Building2 } from "lucide-react";
import { api, extractApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { LEAD_INTEREST } from "../lib/enums";
import { applyBrandColor } from "../lib/brand-context";
import { LEAD_INTEREST_LABELS } from "../lib/labels";
import {
  buildInitialPublicFormState,
  buildPublicDemoPageContent,
  getPublicDemoSubmitButtonLabel,
  validatePublicFormValues,
} from "./publicPageContent";
import { toast } from "sonner";
import ThemeToggle from "../components/theme/ThemeToggle";

export default function PublicDemoPage() {
  const { formKey } = useParams();
  const navigate = useNavigate();
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(() => buildInitialPublicFormState() || {});
  const [formErrors, setFormErrors] = useState({});
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [emailTrackingConsent, setEmailTrackingConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStartedAtMs, setSubmitStartedAtMs] = useState(() => Date.now());

  useEffect(() => {
    api
      .get("/api/public/tenant-info", { params: { form_key: formKey } })
      .then((r) => {
        setTenantInfo(r.data);
        applyBrandColor(r.data.branding_color);
      })
      .catch((e) => setError(extractApiError(e, "Ungültiger Formularlink.")))
      .finally(() => setLoading(false));
  }, [formKey]);

  useEffect(() => {
    if (!tenantInfo?.public_form) return;
    const content = buildPublicDemoPageContent(
      tenantInfo?.default_project || "OptiMES Prime",
      tenantInfo.public_form,
      tenantInfo
    );
    setForm(buildInitialPublicFormState(content.fieldConfig) || {});
    setFormErrors({});
    setPrivacyAccepted(false);
    setEmailTrackingConsent(false);
    setMarketingConsent(false);
    setSubmitStartedAtMs(Date.now());
  }, [tenantInfo]);

  const updateFieldValue = (fieldKey, value) => {
    setForm((current) => ({ ...(current || {}), [fieldKey]: value }));
    setFormErrors((current) => {
      if (!current[fieldKey]) return current;
      const next = { ...current };
      delete next[fieldKey];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const content = buildPublicDemoPageContent(project, tenantInfo?.public_form, tenantInfo);
    const errors = validatePublicFormValues(content.fieldConfig, form) || {};
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error(Object.values(errors)[0] || "Bitte prüfen Sie Ihre Eingaben.");
      return;
    }
    if (!privacyAccepted) {
      toast.error("Bitte bestätigen Sie die Datenschutzerklärung.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/public/leads", {
        form_key: formKey,
        ...form,
        source: "DEMO_REQUEST",
        privacy_accepted: privacyAccepted,
        email_tracking_consent: emailTrackingConsent,
        marketing_consent: marketingConsent,
        website: form.website || "",
        submit_meta: {
          started_at_ms: submitStartedAtMs,
        },
      });
      navigate(`/demo/${formKey}/danke`, { state: { tenantInfo } });
    } catch (err) {
      toast.error(extractApiError(err, "Anfrage konnte nicht gesendet werden."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground bg-background">
        Wird geladen …
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-background">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <div className="font-display text-base font-semibold">Formular nicht gefunden</div>
            <div className="text-sm text-muted-foreground mt-2">{error}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const project = tenantInfo?.default_project || "OptiMES Prime";
  const tenantName = tenantInfo?.tenant_name || "";
  const content = buildPublicDemoPageContent(project, tenantInfo?.public_form, tenantInfo);
  const visibleFields = content.fieldConfig.filter((field) => field.visible);

  return (
    <div className="min-h-screen relative overflow-hidden bg-background text-foreground">
      {/* Ambient layers */}
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" aria-hidden />
      <div className="pointer-events-none absolute inset-0 bg-radial-brand" aria-hidden />

      <header className="relative z-10 border-b border-border bg-background/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 py-3.5 flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-primary-foreground brand-glow"
            style={{ background: "var(--brand)" }}
          >
            <Flame className="w-4 h-4" />
          </div>
          <div className="text-sm font-display font-semibold">
            FyreCRM · <span className="text-muted-foreground font-normal">{tenantName}</span>
          </div>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 py-12">
        <div className="max-w-2xl">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium uppercase tracking-wider"
            style={{
              background: "color-mix(in srgb, var(--brand) 14%, transparent)",
              color: "var(--brand)",
              border: "1px solid color-mix(in srgb, var(--brand) 35%, transparent)",
            }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--brand)" }}
            />
            {content.kicker}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-4 leading-[1.05] text-gradient-brand">
            {content.headline}
          </h1>
          <p className="text-base text-muted-foreground mt-3 leading-relaxed max-w-xl">
            {content.subline}
          </p>
          {content.introText ? (
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-2xl">
              {content.introText}
            </p>
          ) : null}

          {content.isAvailable ? (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-sm">
              <TrustPoint icon={MailCheck} text="Schnelle Rückmeldung" />
              <TrustPoint icon={ShieldCheck} text="DSGVO-konform" />
              <TrustPoint icon={Building2} text="Nur B2B" />
            </div>
          ) : null}
        </div>

        {content.isAvailable ? (
          <Card className="mt-8 backdrop-blur-xl" data-testid="public-demo-form">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <Label htmlFor="public-demo-website">Website</Label>
                  <Input
                    id="public-demo-website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    data-testid="public-demo-website"
                    value={form.website || ""}
                    onChange={(e) => updateFieldValue("website", e.target.value)}
                  />
                </div>
                {visibleFields.map((field) => (
                  <F
                    key={field.key}
                    label={field.required ? `${field.label} *` : field.label}
                    full={field.type === "textarea" || field.type === "select"}
                    helpText={
                      formErrors[field.key] ||
                      (field.key === "interest" ? content.interestHelpText : undefined)
                    }
                  >
                    {field.type === "textarea" ? (
                      <Textarea
                        rows={4}
                        data-testid={`public-demo-${field.key}`}
                        value={form[field.key] || ""}
                        onChange={(e) => updateFieldValue(field.key, e.target.value)}
                        placeholder={field.placeholder || ""}
                      />
                    ) : field.type === "select" ? (
                      <Select
                        value={
                          form[field.key] ||
                          field.options?.[0]?.value ||
                          LEAD_INTEREST.OPTIMES_DEMO
                        }
                        onValueChange={(value) => updateFieldValue(field.key, value)}
                      >
                        <SelectTrigger data-testid={`public-demo-${field.key}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(field.options || []).map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label || LEAD_INTEREST_LABELS[option.value] || option.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={field.type === "tel" ? "tel" : field.type}
                        data-testid={`public-demo-${field.key}`}
                        value={form[field.key] || ""}
                        onChange={(e) => updateFieldValue(field.key, e.target.value)}
                        placeholder={field.placeholder || ""}
                        required={field.required}
                      />
                    )}
                  </F>
                ))}
                <div className="sm:col-span-2 space-y-3 rounded-xl border border-border/70 bg-card/50 p-4">
                  {content.emailTrackingConsentEnabled ? (
                    <label className="flex items-start gap-3 text-sm text-foreground">
                      <Checkbox
                        checked={emailTrackingConsent}
                        onCheckedChange={(checked) => setEmailTrackingConsent(Boolean(checked))}
                        data-testid="public-demo-email-tracking-checkbox"
                        className="mt-0.5"
                      />
                      <span className="whitespace-pre-line">{content.emailTrackingConsentText}</span>
                    </label>
                  ) : null}
                  {content.marketingConsentEnabled ? (
                    <label className="flex items-start gap-3 text-sm text-foreground">
                      <Checkbox
                        checked={marketingConsent}
                        onCheckedChange={(checked) => setMarketingConsent(Boolean(checked))}
                        data-testid="public-demo-marketing-checkbox"
                        className="mt-0.5"
                      />
                      <span className="whitespace-pre-line">{content.marketingConsentText}</span>
                    </label>
                  ) : null}
                  <label className="flex items-start gap-3 text-sm text-foreground">
                    <Checkbox
                      checked={privacyAccepted}
                      onCheckedChange={(checked) => setPrivacyAccepted(Boolean(checked))}
                      data-testid="public-demo-privacy-checkbox"
                      className="mt-0.5"
                    />
                    <span className="whitespace-pre-line">
                      {content.privacyDisclaimerParts.map((part, index) =>
                        part.type === "link" ? (
                          <a
                            key={`${part.text}-${index}`}
                            href={part.href}
                            target={part.href.startsWith("mailto:") ? undefined : "_blank"}
                            rel={part.href.startsWith("mailto:") ? undefined : "noreferrer"}
                            className="underline underline-offset-2 hover:text-foreground"
                          >
                            {part.text}
                          </a>
                        ) : (
                          <React.Fragment key={`${part.text}-${index}`}>{part.text}</React.Fragment>
                        )
                      )}
                    </span>
                  </label>
                  <div className="text-xs text-muted-foreground space-x-3">
                    <a
                      href={content.privacyPolicyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="underline underline-offset-2 hover:text-foreground"
                    >
                      Datenschutzerklärung
                    </a>
                    {content.imprintUrl ? (
                      <a
                        href={content.imprintUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                      >
                        Impressum
                      </a>
                    ) : null}
                  </div>
                  {content.showLegalNotice ? (
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div className="font-medium text-foreground/90">{content.legalNoticeHeading}</div>
                      <div>{content.legalNoticeText}</div>
                    </div>
                  ) : null}
                </div>
                <div className="sm:col-span-2 flex justify-end pt-1">
                  <Button
                    type="submit"
                    disabled={submitting || !privacyAccepted}
                    data-testid="public-demo-submit-button"
                  >
                    {submitting
                      ? getPublicDemoSubmitButtonLabel(true)
                      : content.buttonText || getPublicDemoSubmitButtonLabel(false)}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="mt-8 backdrop-blur-xl" data-testid="public-demo-unavailable">
            <CardContent className="p-6 sm:p-8">
              <div className="font-display text-lg font-semibold">
                Dieses Formular ist derzeit nicht verfügbar.
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                Bitte versuchen Sie es später erneut oder wenden Sie sich direkt an den Anbieter.
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function F({ label, children, full, helpText }) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {helpText ? <div className="text-xs text-muted-foreground">{helpText}</div> : null}
      {children}
    </div>
  );
}

function TrustPoint({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card/60 backdrop-blur">
      <Icon className="w-3.5 h-3.5" style={{ color: "var(--brand)" }} />
      <span className="text-sm">{text}</span>
    </div>
  );
}
